// Customize mode for a transaction — the overlay diffs each gesture produces.
//
// Pure functions over the EFFECTIVE model: the screen calls them and hands the
// diff to the session. Paths are exactly the presentational leaves the
// engine's overlay_policy allows for type "T":
//   transaction.sql_models.@items.<form>.columns.@items.<col>.<leaf>
//   transaction.sql_models.@items.<form>.{form_title, frozen_columns}
//   transaction.sql_models.@items.<form>.group_settings.<group>.{label,order,open}

import type { CustomizeDiff } from './session'

/** Minimal column shape the builders read. */
export interface CustomizeColumn {
  col_id?: string
  db_name?: string
  name?: string
  heading?: string
  list_heading?: string
  group_name?: string
  x?: string | number
  y?: string | number
  width?: number | string
  hidden?: boolean | string
  list_hidden?: boolean | string
  protect?: boolean | string
  mandatory?: boolean | string
  key?: boolean | string
  default_value?: string
  sql_expression?: string
}

export function columnKey(c: CustomizeColumn): string {
  return String(c.col_id || c.db_name || c.name || '')
}

/** The key `group_settings` uses for a group ('' → "Others"). */
export function groupKey(name: string | undefined | null): string {
  return String(name ?? '').trim() || 'Others'
}

export const formBase = (formNo: string) => `transaction.sql_models.@items.${formNo}`
export const colPath = (formNo: string, colKey: string, leaf: string) =>
  `${formBase(formNo)}.columns.@items.${colKey}.${leaf}`
export const formPath = (formNo: string, leaf: string) => `${formBase(formNo)}.${leaf}`
export const groupPath = (formNo: string, group: string, leaf: string) =>
  `${formBase(formNo)}.group_settings.${groupKey(group)}.${leaf}`

export function truthy(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') return ['1', 'true', 'yes', 'y'].includes(v.trim().toLowerCase())
  return false
}

const AUTO_DEFAULT = /^\s*(auto_generate[a-z_]*\(\)|:audit_[a-z_]+)\s*$/i

/** Why a field's "editable" / "default" cannot be changed, or null. */
export function appOwnedReason(c: CustomizeColumn): string | null {
  if (truthy(c.key)) return 'Key field — always read-only'
  if (AUTO_DEFAULT.test(String(c.default_value ?? ''))) return 'Filled by the application'
  if (String(c.sql_expression ?? '').trim()) return 'Calculated — always read-only'
  return null
}

const num = (v: unknown, fallback: number) => {
  const n = Number(v)
  return Number.isFinite(n) && String(v ?? '').trim() !== '' ? n : fallback
}

function stampGroupOrder(
  formNo: string, groupOrder: string[], settings: Record<string, { order?: unknown }> | undefined,
  diff: CustomizeDiff,
) {
  // Field moves renumber y, and the form otherwise orders groups by their
  // first field's y — pin every group's position the first time anything
  // moves, so a move never reshuffles the groups.
  const anyPinned = groupOrder.some((g) => settings?.[groupKey(g)]?.order != null)
  if (anyPinned) return
  groupOrder.forEach((g, i) => diff.set.push({ path: groupPath(formNo, g, 'order'), value: (i + 1) * 10 }))
}

/**
 * Move a field to `toGroup`, before `beforeKey` (or to the group's end).
 * `groups` = the form's groups in display order with their columns in
 * display order (FormRenderer's `groupColumns`).
 */
export function moveFieldDiff(opts: {
  formNo: string
  groups: Array<[string, CustomizeColumn[]]>
  settings?: Record<string, { order?: unknown }>
  moving: CustomizeColumn
  toGroup: string
  beforeKey: string | null
}): CustomizeDiff {
  const { formNo, groups, settings, moving, toGroup, beforeKey } = opts
  const diff: CustomizeDiff = { set: [], reset: [] }
  const movingKey = columnKey(moving)
  const target = groups.find(([g]) => g === toGroup)
  if (!target) return diff
  const members = target[1].filter((c) => columnKey(c) !== movingKey)
  const at = beforeKey ? members.findIndex((c) => columnKey(c) === beforeKey) : -1
  const ordered = [...members]
  ordered.splice(at < 0 ? ordered.length : at, 0, moving)
  // Keep the group's first y where it was so its own position never changes.
  const ys = target[1].map((c) => num(c.y, 0))
  const base = ys.length ? Math.min(...ys) : num(moving.y, 0)
  ordered.forEach((c, i) => {
    const y = String(Math.round((base + i * 0.01) * 1000) / 1000)
    if (String(c.y ?? '') !== y) diff.set.push({ path: colPath(formNo, columnKey(c), 'y'), value: y })
  })
  if (groupKey(moving.group_name) !== groupKey(toGroup)) {
    // The real group_name a member of the target group carries ('' vs "Others").
    const raw = members[0]?.group_name ?? (toGroup === 'Others' ? '' : toGroup)
    diff.set.push({ path: colPath(formNo, movingKey, 'group_name'), value: raw ?? '' })
  }
  stampGroupOrder(formNo, groups.map(([g]) => g), settings, diff)
  return diff
}

/** Move a group one place up (-1) or down (+1). */
export function moveGroupDiff(formNo: string, groupOrder: string[], group: string, dir: -1 | 1): CustomizeDiff {
  const i = groupOrder.indexOf(group)
  const j = i + dir
  const diff: CustomizeDiff = { set: [], reset: [] }
  if (i < 0 || j < 0 || j >= groupOrder.length) return diff
  const next = [...groupOrder]
  ;[next[i], next[j]] = [next[j], next[i]]
  next.forEach((g, k) => diff.set.push({ path: groupPath(formNo, g, 'order'), value: (k + 1) * 10 }))
  return diff
}

export function setLeaf(path: string, value: unknown): CustomizeDiff {
  return { set: [{ path, value }], reset: [] }
}

export function resetLeaf(path: string): CustomizeDiff {
  return { set: [], reset: [path] }
}

/** Set a leaf, or reset it when the value equals the core's. */
export function leafDiff(path: string, value: unknown, coreValue: unknown): CustomizeDiff {
  const same = (a: unknown, b: unknown) => {
    if (typeof a === 'boolean' || typeof b === 'boolean') return truthy(a) === truthy(b)
    return String(a ?? '') === String(b ?? '')
  }
  return same(value, coreValue) ? resetLeaf(path) : setLeaf(path, value)
}

/** Re-order the list (grid) columns: x = (i+1)*10 in the given order. */
export function listOrderDiff(formNo: string, ordered: CustomizeColumn[]): CustomizeDiff {
  const diff: CustomizeDiff = { set: [], reset: [] }
  ordered.forEach((c, i) => {
    const x = String((i + 1) * 10)
    if (String(c.x ?? '') !== x) diff.set.push({ path: colPath(formNo, columnKey(c), 'x'), value: x })
  })
  return diff
}

// ── reading the effective / core model ───────────────────────────────────

const unwrap = (x: any, wrapper: string) =>
  x && typeof x === 'object' && x[wrapper] && Object.keys(x).length === 1 ? x[wrapper] : x

export function modelForms(model: any): any[] {
  const txn = model?.transaction ?? model
  return (txn?.sql_models || []).map((s: any) => unwrap(s, 'sql_model'))
}

export function modelForm(model: any, formNo: string): any | undefined {
  return modelForms(model).find((f) => String(f?.form_no ?? '1') === String(formNo))
}

export function formColumns(form: any): CustomizeColumn[] {
  return (form?.columns || []).map((c: any) => unwrap(c, 'column')).filter(Boolean)
}

export function coreColumn(core: any, formNo: string, colKey: string): CustomizeColumn | undefined {
  return formColumns(modelForm(core, formNo)).find((c) => columnKey(c) === colKey)
}
